/**
 * Materiais agrupados para um seletor.
 *
 * Um laboratório com o catálogo carregado passa de cinquenta materiais, e uma lista corrida obriga
 * a ler tudo pra achar uma embalagem. O agrupamento usa a **categoria do laboratório** (Brocas,
 * Resinas, Embalagem e envio) porque é como ele organiza o estoque de fato; material que ainda não
 * foi organizado cai na subcategoria química, que ao menos diz do que ele é feito — e nunca num
 * balde anônimo.
 *
 * Categorias primeiro, químicas depois: o que a pessoa escolheu vem antes do que o sistema supôs.
 *
 * Vive em `lib` porque três telas precisam do mesmo agrupamento — ficha técnica, consumo do serviço
 * e entrada de estoque — e a terceira cópia da mesma regra é onde elas começam a divergir.
 */

export type MaterialAgrupavel = {
  id: string;
  name: string;
  active?: boolean;
  subcategory?: { label: string } | null;
  materialCategory?: { label: string } | null;
  manufacturer?: { name: string } | null;
};

export type GrupoDeMaterial<T> = { label: string; itens: T[] };

/** Só material ativo: oferecer o que o laboratório desligou é convidar a um erro silencioso. */
export function agruparMateriais<T extends MaterialAgrupavel>(materiais: T[]): GrupoDeMaterial<T>[] {
  const mapa = new Map<string, { label: string; ordem: number; itens: T[] }>();

  for (const m of materiais) {
    if (m.active === false) continue;
    const label = m.subcategory?.label ?? m.materialCategory?.label ?? "Outros";
    const ordem = m.subcategory ? 0 : 1;
    if (!mapa.has(label)) mapa.set(label, { label, ordem, itens: [] });
    mapa.get(label)!.itens.push(m);
  }

  return [...mapa.values()]
    .sort((a, b) => a.ordem - b.ordem || a.label.localeCompare(b.label, "pt-BR"))
    .map(({ label, itens }) => ({
      label,
      itens: [...itens].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    }));
}

/** "Bloco C14 (Talmax)" — o fabricante distingue homônimos de marcas diferentes. */
export const rotuloDoMaterial = (m: MaterialAgrupavel) =>
  m.manufacturer?.name ? `${m.name} (${m.manufacturer.name})` : m.name;
